import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PlatformDetect } from '@libs/platform/platform-detect';
import { CONFIG_OPTIONS, ConfigInterface } from '@libs/config/config-options';
import { catchError, timeout } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import { FactoryService } from '@services/common/factory.service';
import { Base64 } from 'js-base64';
import { ApiLibs } from '@libs/config/api-library';
import { LogInfo } from '@models/com-model';
import { JSBridgeService } from './jsbridge.service';
import { v4 as uuidv4 } from 'uuid';
import { ComRouteService } from './com-route.service';
import ProfileSdk from "@shdr/profile-sdk";
import { ProfileSdkService } from './profile-sdk.service';
import { ComSubService } from './com-sub.service';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  public profileSdk: ProfileSdk;
  public haveInitSDK: boolean = false; //control to init SDK
  public token: string;
  public refreshToken: string;
  public refreshed: boolean = false;

  public isOaFans: string; //use check user is wechat OA fans
  public isApHolder: string;//use check user is ap_hub holder
  private checkedAPHolder: boolean = false;//use mark user checkeed is ap_hub holder or not
  public dateOfBirth: string;
  public firstName: string;
  public lastName: string;
  public residenceCountryName: string = '中国内地';
  public logInfo: LogInfo = {
    mobile: null,
    areaNum: null,
    email: null
  };
  public functionType: string;
  public legacyVerify: boolean;
  public legacyLoginName: string;
  public registerCouponList: any;  // register coupon

  constructor(
    private http: HttpClient,
    private platform: PlatformDetect,
    private factorySer: FactoryService,
    private jsBridgeSer: JSBridgeService,
    private comRoute: ComRouteService,
    private profileSdkSer: ProfileSdkService,
    private comSubSer: ComSubService,
    @Inject(CONFIG_OPTIONS) private configOptions: ConfigInterface,
  ) { 
    this.initSDK();
  }

  async initSDK() { 
    if (!this.haveInitSDK) { 
      this.profileSdk = this.profileSdkSer.getInstance();
      const lang = await this.factorySer.getLanguage();
      const sdkEnv = this.factorySer.getEnv(true);
      console.log('initSDK env', sdkEnv);
      this.profileSdk.init({
        env: sdkEnv,
        language: lang,
        servicePrefix: this.configOptions.tykBaseDomain('/token')
      });
      this.haveInitSDK = true;
    }
  }

  public getBaseToken(token: string): string {
    if (token) {
      const chalCode = this.factorySer.getChannel();
      const busCode = this.factorySer.getBusiness();
      return Base64.encode(`${token}&${chalCode}&${busCode}`);
    } else { 
      return '';
    }
  }

  /**
   * 通过sdk获取token信息
   * @param refresh 是否需要刷新token
   * @param current 是否始终从当前cookie中获取对应的token信息
   * @param toLogin control whether go to login or not
   * @returns 
   */
  async getAccessToken(refresh?: boolean, current?: boolean, toLogin: boolean = true) {
    if (refresh || !this.token || current) {
      const body = {
        type: this.platform.getPlatform(),
        openId: this.factorySer.getOpenId(),
        unionId: this.factorySer.getUnionId(),
        channelCode: this.factorySer.getChannel(),
        businessCode: this.factorySer.getBusiness(),
        forceUpdate: refresh || false, // 是否强刷token
        goLoginPage: false
      };
      console.log('call sdk body..', body);
      const resp = await this.profileSdk.getAccessToken(body);
      if (resp?.code === 0) {
        // refresh-token fail will go to login page
        if (resp?.data?.accessToken && resp.data.accessToken?.length > 0) {
          this.token = resp?.data?.accessToken;
          return this.token;
        } else {
          if (toLogin) { 
            this.comRoute.checkToLogin();
          }
          this.token = null;
          return null;
        }
      }
    } else {
      return this.token || null;
    }
  }

  //check token avalibility
  async validateToken(token: string): Promise<any> { 
    const url = this.configOptions.tykBaseUrl(ApiLibs.validateToken,'cu','ctoken-service');
    const body = { encodedAccessToken: Base64.encode(`${token}&${this.factorySer.getChannel()}&${this.factorySer.getBusiness()}`) };
    return firstValueFrom(this.http.post<any>(url, body)
      .pipe(timeout(10000), catchError(err => of(err))), { defaultValue: '' });
  }

  async fetchConfigs(): Promise<any> {
    const url = this.configOptions.tykBaseUrl(ApiLibs.getConfigs);
    return firstValueFrom(this.http.post<any>(url, {})
      .pipe(timeout(10000), catchError(err => of(err))), { defaultValue: '' });
  }

  async loginRegister(type: string, params: any): Promise<any> {
    sessionStorage.setItem('lgType', type);
    const body = await this.getLoginBody(type, params);
    const apiPath = (type === 'phonePin' || type === 'legacyPhonePin' || type === 'cspPhonePin') ? ApiLibs.loginPhonePin :
      (type === 'phonePwd' ? ApiLibs.loginPhonePwd : ApiLibs.loginEmailPwd);
    const url = this.configOptions.tykBaseUrl(apiPath);
    return firstValueFrom(this.http.post<any>(url, body, {
      headers: {
        "Cache-Control": 'no-store',
        "Pragma": "no-cache"
      },
      withCredentials: true
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  private async getLoginBody(type: string, params: any): Promise<any> { 
    const passInfo = JSON.parse(sessionStorage.getItem('passInfo'));
    const selectIterms = this.factorySer.getSessionInfo('selectIterms', true);
    const countryCodeData = params?.areaNum?.split(',') || passInfo?.areaNum?.split(',');
    let body = {
      loginName: params?.mobile || passInfo && passInfo?.mobile,
      mobileAreaNum: type === 'emailPwd'? null : countryCodeData[0],
      mobileCountryISO2: type === 'emailPwd'? null : countryCodeData[1],
      password: (type === 'phonePin' || type === 'cspPhonePin') ? null : this.factorySer.encryptCode(params?.password),
      passwordEncrypted: (type === 'phonePin' || type === 'cspPhonePin') ? null : this.factorySer.encryptCode(null, true),
      dateOfBirth: this.dateOfBirth || null,
      legacyVerify: false,
      adultConfirm: selectIterms?.age,
      deviceId: this.getDeviceId(),
      openId: this.factorySer.getOpenId(),
      unionId: this.factorySer.getUnionId(),
      acceptedRegulations: true
    };
    // get device info in APP for 'device limitation' feature
    if (this.factorySer.isAbleVersion('deviceInfoVer')) { 
      const resp = await this.jsBridgeSer?.callJSBridge('getSystemInfo');
      if (resp?.resultCode === 0) {
        body['deviceId'] = resp?.deviceId || null;
        body['deviceModel'] = resp?.deviceModel || null;
        body['deviceBrand'] = this.platform.isDisneyIOS()? 'Apple' : (resp?.deviceBrand || null);
        body['deviceSystemVersion'] = resp?.deviceSystemVersion || null;
        body['signature'] = resp?.signature || null;
        body['timestamp'] = resp?.timestamp || null;
        body['nonce'] = resp?.nonce || null;
      }
    }
    switch (type) { 
      case 'emailPwd':
        body.loginName = params?.email;
        body['email'] = params?.email;
        break;
      case 'phonePin':
        body['pinCode'] = params?.pinCode || Base64.decode(passInfo?.pinCode);
        break;
      case 'cspPhonePin':
        body['pinCode'] = params?.pinCode || Base64.decode(passInfo?.pinCode);
        body['inviteCode'] = params?.inviteCode;
        break;
      case 'legacyPhonePin':
        body.legacyVerify = this.legacyVerify;
        body.password = this.factorySer.encryptCode(params?.password);
        body['legacyLoginName'] = this.legacyLoginName;
        body['pinCode'] = params?.pinCode || Base64.decode(passInfo?.pinCode);
        break;
      default:
        break;
    }
    return body;
  }

  private getDeviceId(): string { 
    if (this.platform.isWeb()) {
      return uuidv4();
    } else if (this.platform.isApp()) {
      this.jsBridgeSer.callJSBridge('getDeviceID', {}, res => { 
        console.log('getDeviceID..',res);
        return res?.deviceID;
      });
    } else { 
      return localStorage.getItem('uuid');
    }
    return this.platform.isWeb() ? uuidv4() : (this.platform.isWx() ? localStorage.getItem('uuid') : '');
  }

  //only use for visitor-mode
  async visitorLogin(): Promise<any> {
    const body = this.getWxBody();
    const url = this.configOptions.tykBaseUrl(ApiLibs.visitorLogin);
    return firstValueFrom(this.http.post<any>(url, body, {
      headers: {
        "Cache-Control": 'no-store',
        "Pragma": "no-cache"
      },
      withCredentials: true
    }).pipe(timeout(10000), catchError(err => of(err))), { defaultValue: '' });
  }
  
  // verify invite code for activating temp account
  async checkInviteCode(inviteCode: string): Promise<any> {
    const url = this.configOptions.tykBaseUrl(ApiLibs.verifyInviteCode);
    const params = {
      inviteCode
    }
    return firstValueFrom(this.http.post(url, params)
      .pipe(timeout(10000), catchError(err => of(err))), { defaultValue: '' });
  }

  // trigger diff api base on action (send/verify)
  async triggerPinInfo(body: any, action?: string): Promise<any> { 
    let url = '';
    switch (action) { 
      case 'send':
        const pinMode = this.factorySer.pinMode;
        url = pinMode === 'Voice'? ApiLibs.sendVoicePin : ApiLibs.sendPin;
        break;
      case 'verify':
        url = ApiLibs.verifyPin;
        break;
      default:
        url = ApiLibs.getPinExpireInfo;
        break;
    }
    return firstValueFrom(this.http.post<any>(this.configOptions.tykBaseUrl(url), body)
      .pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  // get/check pin-code info only for logined account
  async triggerLoggedPin(params: any, action?: string): Promise<any> {
    const resp = await this.toTriggerLoggedPin(params, this.token || await this.getAccessToken(), action);
    const result = await this.checkTokenExpire(resp);
    if (result?.expired) {
      return this.triggerLoggedPin(params, action);
    } else{
      return result?.res || null;
    }
  }

  async toTriggerLoggedPin(params: any, token: string, action?: string): Promise<any> {
    params['functionType'] = 'DELETE_ACCOUNT_MOBILE';
    let url = '';
    switch (action) {
      case 'send':
        const pinMode = this.factorySer.pinMode;
        url = pinMode === 'Voice'? ApiLibs.sendLoggedVoicePin : ApiLibs.sendLoggedPin;
        break;
      case 'verify':
        url = ApiLibs.verifyLoggedPin;
        break;
      default:
        url = ApiLibs.getLoggedPinExpireInfo;
        // 获取倒计时不需要captcha参数
        params['params'] = null;
        break;
    }
    return firstValueFrom(this.http.post(this.configOptions.tykBaseUrl(url), params, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      }
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  async getProvinces(): Promise<any> {
    const url = this.configOptions.tykBaseUrl(ApiLibs.getProvices);
    const body = {
      includePopular: true
    }
    return firstValueFrom(this.http.post<any>(url, body)
      .pipe(timeout(10000), catchError(err => of(err))), { defaultValue: '' });
  }

  async getCountryCallCodes(): Promise<any> {
    const url = this.configOptions.tykBaseUrl(ApiLibs.getCountryCallCodes);
    const body = {includePopular: true};
    return firstValueFrom(this.http.post<any>(url, body)
      .pipe(timeout(10000), catchError(err => of(err))), { defaultValue: '' });
  }

  async getCountryRegions(): Promise<any> {
    const url = this.configOptions.tykBaseUrl(ApiLibs.getCountryRegions);
    const body = {includePopular: true};
    return firstValueFrom(this.http.post<any>(url, body)
      .pipe(timeout(10000), catchError(err => of(err))), { defaultValue: '' });
  }

  async completeInfo(params: any): Promise<any> {
    const resp = await this.triggerCompleteInfo(params, this.token || await this.getAccessToken());
    const result = await this.checkTokenExpire(resp);
    if (result?.expired) {
      return this.completeInfo(params);
    } else{
      return result?.res || null;
    }
  }

  async triggerCompleteInfo(params: any, token: string) {
    return firstValueFrom(this.http.post(this.configOptions.tykBaseUrl(ApiLibs.completeInfo), params, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      }
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  // check email/phone/passwod/name availability
  async validateVerify(params: any, logined: boolean = false) {
    if (logined) {
      const resp = await this.fetchValidateVerify(params, this.token || await this.getAccessToken());
      const result = await this.checkTokenExpire(resp);
      if (result?.expired) {
        return this.validateVerify(params, logined);
      } else{
        return result?.res || null;
      }
    } else { 
      const resp = await this.fetchValidateVerify(params);
      return resp;
    }
    
  }

  async fetchValidateVerify(params: any, token?: string) {
    return firstValueFrom(this.http.post(this.configOptions.tykBaseUrl(ApiLibs.validateVerify), params, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      }
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  async triggerUserInfo(): Promise<any> { 
    // 个人中心页需每次获取最新值
    const resp = await this.fetchUserInfo(await this.getAccessToken(false,true));
    const result = await this.checkTokenExpire(resp,true);
    if (result?.expired) {
      return this.triggerUserInfo(); // refreshed token, then re-trigger API
    } else{
      return result?.res || null;
    }
  }

  async fetchUserInfo(token: string): Promise<any>  { 
    const path = this.platform.isWx() ? ApiLibs.getWxUserInfo : (this.platform.isApp() ? ApiLibs.getAppUserInfo : ApiLibs.getUserInfo);
    const tykDomainKey = this.platform.isWx() ? 'wec' : (this.platform.isApp()? 'app' : 'cu');
    const url = this.configOptions.tykBaseUrl(path, tykDomainKey);
    return firstValueFrom(this.http.post<any>(url, {}, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      },
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  async getAvavarImg(id: string): Promise<any> {
    const body = {avatarId: id}
    const url = this.configOptions.tykBaseUrl(ApiLibs.getAvatarImg);
    return firstValueFrom(this.http.post<any>(url, body).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  async getAvatars(): Promise<any> { 
    const url = this.configOptions.tykBaseUrl(ApiLibs.getAvatars);
    return firstValueFrom(this.http.post<any>(url, {}).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  async doUpdateProfile(path: string, body: any, key?: string,): Promise<any> { 
    const logined = this.factorySer.getLoginStatus();
    console.log('doUpdateProfile logined..', logined);
    if (!logined && key === 'resetPwd') {
      const resp = await this.triggerUpdateProfile(null, path, body);
      return resp;
    } else { 
      const token = logined ? (this.token || await this.getAccessToken()) : await this.getAccessToken();
      const resp = await this.triggerUpdateProfile(token, path, body);
      const result = await this.checkTokenExpire(resp);
      if (result?.expired) {
        return this.doUpdateProfile(path, body);
      } else {
        return result?.res || null;
      }
    }
  }

  async triggerUpdateProfile(token: string, path: string, body: any): Promise<any>  { 
    const url = this.configOptions.tykBaseUrl(path);
    return firstValueFrom(this.http.post<any>(url, body, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      },
      withCredentials: true
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  async doLogout(): Promise<any> { 
    let body = {};
    if (this.platform.isWx()) { 
      body = this.getWxBody();
    }
    const resp = await this.triggerLogout(this.token || await this.getAccessToken(), body);
    const result = await this.checkTokenExpire(resp);
    if (result?.expired) {
      return this.doLogout();
    } else{
      return result?.res || null;
    }
  }

  async triggerLogout(token: string, body: any): Promise<any>  { 
    const url = this.configOptions.tykBaseUrl(ApiLibs.doLogout);
    return firstValueFrom(this.http.post<any>(url, body, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      },
      withCredentials: true
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  async deleteAcct(body: any): Promise<any> { 
    const resp = await this.doDeleteAcct(this.token || await this.getAccessToken(), body);
    const result = await this.checkTokenExpire(resp);
    if (result?.expired) {
      return this.deleteAcct(body);
    } else{
      return result?.res || null;
    }
  }

  async doDeleteAcct(token: string, body: any): Promise<any>  { 
    const url = this.configOptions.tykBaseUrl(ApiLibs.doDeleteAcct);
    return firstValueFrom(this.http.post<any>(url, body, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      },
      withCredentials: true
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  async getAcctBindingStatus(): Promise<any> { 
    const resp = await this.queryAcctBindingStatus(this.token || await this.getAccessToken());
    const result = await this.checkTokenExpire(resp);
    if (result?.expired) {
      return this.getAcctBindingStatus();
    } else{
      return result?.res || null;
    }
  }

  async queryAcctBindingStatus(token: string): Promise<any>{ 
    const url = this.configOptions.tykBaseUrl(ApiLibs.queryAcctBindingStatus);
    return firstValueFrom(this.http.post<any>(url, {}, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      },
      withCredentials: true
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }


  async qureyQRCode(): Promise<any> { 
    const resp = await this.generateQRCode(this.token || await this.getAccessToken());
    const result = await this.checkTokenExpire(resp);
    if (result?.expired) {
      return this.qureyQRCode();
    } else{
      return result?.res || null;
    }
  }

  async generateQRCode(token: string): Promise<any>{ 
    const url = this.configOptions.tykBaseUrl(ApiLibs.generateQRCode);
    return firstValueFrom(this.http.post<any>(url, {}, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      },
      withCredentials: true
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  async triggerAcctBinding(body: any): Promise<any> { 
    const resp = await this.acctBinding(this.token || await this.getAccessToken(), body);
    const result = await this.checkTokenExpire(resp);
    if (result?.expired) {
      return this.triggerAcctBinding(body);
    } else{
      return result?.res || null;
    }
  }

  async acctBinding(token: string, body: any): Promise<any>{ 
    const url = this.configOptions.tykBaseUrl(ApiLibs.acctBinding);
    return firstValueFrom(this.http.post<any>(url, body, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      },
      withCredentials: true
    }).pipe(timeout(60000), catchError(err => of(err))), { defaultValue: '' });
  }

  async apCheck(): Promise<any> { 
    const resp = await this.doAPCheck(this.token || await this.getAccessToken());
    const result = await this.checkTokenExpire(resp);
    if (result?.expired) {
      return this.apCheck();
    } else{
      return result?.res || null;
    }
  }

  async doAPCheck(token: string): Promise<any>  { 
    const url = this.configOptions.tykBaseUrl(ApiLibs.doAPCheck, 'cu','profile-ap-service');
    return firstValueFrom(this.http.post<any>(url, {}, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      },
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  async oaFansCheck(): Promise<any> { 
    const resp = await this.doOaFansCheck(this.token || await this.getAccessToken());
    const result = await this.checkTokenExpire(resp);
    if (result?.expired) {
      return this.oaFansCheck();
    } else{
      return result?.res || null;
    }
  }

  async doOaFansCheck(token: string): Promise<any>  { 
    const url = this.configOptions.tykBaseUrl(ApiLibs.doOaFansCheck, 'cu');
    return firstValueFrom(this.http.post<any>(url, {}, {
      headers: {
        'x-disney-ctoken': this.getBaseToken(token)
      },
    }).pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  //get wechat state for non silent authorize
  async getWxState(body: any): Promise<any> { 
    const url = this.configOptions.tykBaseUrl(ApiLibs.getGenState);
    return firstValueFrom(this.http.post<any>(url, body).
    pipe(catchError(err => of(err))), { defaultValue: '' });
  }

  /**
   * check token expired
   * @param checked: only use in my-center page to get user-info and aways is 'true', means checked token expired status, don't check token with getAccessToken again when 0315BT007
   * 0315BT003 accessToken expired, go to refresh token and re-trigger API
   * 0315BT007 accessToken & refreshToken both expired => go to login
   * else err code show system err
   * 
   * refreshed countrl only refresh one times in same time
  */
  public async checkTokenExpire(resp: any, checked?:boolean) {
    if (!resp) return null;
    const errCode = resp?.error?.code || resp?.code;
    if ((errCode === '0315BT003' || errCode === '0315BT007') && !this.refreshed) { 
      if (errCode === '0315BT003') { // accessToken expired, to do refresh token
        this.refreshed = true;
        //if account deleted in other device, token will be null, and getAccessToken will don't go to login
        this.token = await this.getAccessToken(true);
        if(this.token) {
          return { expired: true };
        } else {
          this.comRoute.checkToLogin(true,false); // when token is null means account have no login status, so need to do login 
          return null;
        }
      } else if (errCode === '0315BT007') { //accessToken and refreshToken both expired, go to login 
        if (this.platform.isApp()) { //remove login status in app for device-limitation
          this.token = null;
          this.factorySer.setLoginStatus(false);
          this.comSubSer.refreshLoading(false);
        }
        await this.sdkSignout();
        this.refreshed = true;
        // 除获取信息接口外，其他接口都需要二次获取token
        if(!checked) {
          this.token = await this.getAccessToken(true); // getAccessToken will to do login if get token is null
        }
        
        //if account deleted in other device, token will be null
        if(this.token) {
          return { expired: true };
        } else {
          if (checked) { 
            this.comRoute.checkToLogin(true,false); //when token is null, means account have no login status, so need to do login
          }
          return null;
        }
      } else { 
        this.factorySer.setSinglePop();
        return null
      }
    } else{
      this.refreshed = false;
      return { expired: false, res: resp };
    }
  }

  private async sdkSignout() { 
    const body = {
        channelCode: this.factorySer.getChannel(),
        businessCode: this.factorySer.getBusiness(),
        type: this.platform.getPlatform()
    }
    await this.profileSdk.signout(body); 
  }

  private getWxBody() { 
    return {
      unionId: this.factorySer.getUnionId(),
      openId: this.factorySer.getOpenId(),
      cupUserId: this.factorySer.getSessionInfo('cupUserId', false, true)
    }
  }

  // 微信一键登录
  async wechatAuthLogin(params){
    const url = this.configOptions.tykBaseUrl(ApiLibs.wechatAuthLogin, 'wec');
    return firstValueFrom(this.http.post<any>(url, params, {
      headers: {
        "Cache-Control": 'no-store',
        "Pragma": "no-cache"
      },
      withCredentials: true
    }).pipe(timeout(30000), catchError(err => of(err))), { defaultValue: '' });
  }

  // 微信平台下，获取用户是否关注公众号
  async getOaFans(): Promise<any> {
    const logined = this.factorySer.getLoginStatus();
    if (this.platform.isWx() && logined) {
      const resp = await this.oaFansCheck();
      if (resp?.code === '0') {
        this.isOaFans = resp?.data ? 'Yes' : 'No';
      } else {
        this.isOaFans = 'NA';
      }
    }
    return this.isOaFans;
  }

  // 用户是否为年卡用户
  async getApHolder() {
    if (!this.checkedAPHolder) { 
      this.checkedAPHolder = true;
      const logined = this.factorySer.getLoginStatus();
      if (logined) {
        const resp = await this.apCheck();
        if (resp?.code === '0') {
          this.isApHolder = resp?.data.isApHolder ? 'Yes' : 'No';
        } else {
          this.isApHolder = 'NA';
        }
      }
    }
    return this.isApHolder;
  }
}